import { useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

export const ImageUploadDropzone = ({ 
    file, 
    previewUrl: externalPreviewUrl, // URL externa para mostrar imagen existente
    onFileChange, 
    onFileRemove,
    maxSize = 5 * 1024 * 1024, // 5MB por defecto
    acceptedFormats = ['image/jpeg', 'image/jpg', 'image/png'],
    disabled = false,
    existingImageLabel = null // Etiqueta opcional para imagen existente
}) => {
    
    const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
        // Manejar archivos rechazados
        if (rejectedFiles.length > 0) {
            const error = rejectedFiles[0].errors[0];
            if (error.code === 'file-too-large') {
                toast.error(`El archivo es muy grande. Tamaño máximo: ${maxSize / 1024 / 1024}MB`);
            } else if (error.code === 'file-invalid-type') {
                toast.error('Formato de archivo no válido. Solo se permiten PNG, JPG y JPEG.');
            } else {
                toast.error('Error al cargar el archivo.');
            }
            return;
        }

        // Manejar archivo aceptado
        if (acceptedFiles.length > 0) {
            const selectedFile = acceptedFiles[0];
            onFileChange(selectedFile);
        }
    }, [maxSize, onFileChange]);

    const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
        onDrop,
        accept: {
            'image/*': acceptedFormats.map(format => format.replace('image/', '.'))
        },
        maxSize,
        multiple: false,
        disabled
    });

    // Crear preview URL cuando hay un archivo o usar la URL externa
    const previewUrl = file ? URL.createObjectURL(file) : externalPreviewUrl;
    const hasImage = file || externalPreviewUrl;

    // Limpiar la URL cuando el componente se desmonte o el archivo cambie
    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    return (
        <div className='w-full'>
            {!hasImage ? (
                <div
                    {...getRootProps()}
                    className={`
                        flex justify-center rounded-lg border-2 border-dashed px-6 py-10 
                        transition-all duration-200 cursor-pointer
                        ${isDragActive && !isDragReject ? 'border-indigo-500 bg-indigo-50' : ''}
                        ${isDragReject ? 'border-red-500 bg-red-50' : ''}
                        ${!isDragActive && !isDragReject ? 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50' : ''}
                        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                >
                    <input {...getInputProps()} />
                    <div className="text-center">
                        <PhotoIcon 
                            className={`mx-auto h-12 w-12 transition-colors ${
                                isDragActive ? 'text-indigo-500' : 'text-gray-300'
                            }`} 
                            aria-hidden="true" 
                        />
                        <div className="mt-4 text-sm leading-6 text-gray-600">
                            {isDragActive ? (
                                <p className="font-semibold text-indigo-600">
                                    Suelta la imagen aquí
                                </p>
                            ) : isDragReject ? (
                                <p className="font-semibold text-red-600">
                                    Archivo no válido
                                </p>
                            ) : (
                                <>
                                    <p className="font-semibold text-indigo-600">
                                        Haz clic para seleccionar o arrastra tu comprobante de pago
                                    </p>
                                    <p className="mt-1">PNG, JPG o JPEG</p>
                                </>
                            )}
                        </div>
                        <p className="text-xs leading-5 text-gray-600 mt-2">
                            Tamaño máximo: {maxSize / 1024 / 1024}MB
                        </p>
                    </div>
                </div>
            ) : (
                <div className='relative group'>
                    {/* Etiqueta opcional para imagen existente */}
                    {existingImageLabel && !file && (
                        <div className="mb-3 flex items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 bg-rose-100 text-rose-700 px-3 py-1.5 rounded-lg font-semibold text-sm">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                {existingImageLabel}
                            </span>
                        </div>
                    )}
                    
                    <img
                        src={previewUrl}
                        alt="Vista previa"
                        className="mt-4 mx-auto w-full max-w-md rounded-lg shadow-md transition-all duration-300 group-hover:shadow-xl"
                    />
                    
                    {/* Overlay con información del archivo */}
                    <div className="mt-4 flex items-center justify-between bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex-shrink-0">
                                <PhotoIcon className="h-8 w-8 text-indigo-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                {file ? (
                                    <>
                                        <p className="text-sm font-semibold text-gray-900 truncate">
                                            {file.name}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {(file.size / 1024 / 1024).toFixed(2)} MB
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-sm font-semibold text-gray-900">
                                            Comprobante actual
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            Imagen existente
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>
                        
                        {!disabled && (
                            <button
                                type="button"
                                onClick={onFileRemove}
                                className="flex-shrink-0 ml-4 p-2 bg-white border-2 border-gray-300 rounded-lg hover:border-red-500 hover:bg-red-50 transition-all duration-200 group/btn"
                                title={file ? "Eliminar imagen seleccionada" : "Cambiar comprobante"}
                            >
                                <XMarkIcon className="h-5 w-5 text-gray-500 group-hover/btn:text-red-600" />
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
